import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ServiceOrdersService, ServiceOrderExportRequest, ServiceOrderDashboardData } from '../../../services/service-orders.service';
import { ConfigOrdersService, ConfigOrder as ConfigOrderDTO } from '../../../services/config-orders.service';
import { StatusServiceOrdersService, StatusServiceOrder } from '../../../services/status-service-orders.service';
import { DepartmentService } from '../../../services/department.service';
import { AuthService } from '../../../services/auth.service';
import {
  filterAccessibleConfigOrders,
  mergeAccessibleConfigOrderIds
} from '../../../utils/config-order-access.util';
import { ServiceOrder } from '../../../models/ServiceOrder';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import {
  FALLBACK_SERVICE_ORDER_EXPORT_COLUMNS,
  isDefaultServiceOrderExportKey,
  SERVICE_ORDER_EXPORT_COLUMN_LABELS
} from '../../../shared/constants/service-order-export-columns';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Limite de registros buscados por tipo de OS no modo multi-ID (paginação cliente)
const MAX_ORDERS_PER_CONFIG = 100;

@Component({
  selector: 'app-service-orders-list',
  templateUrl: './service-orders-list.component.html',
  styleUrls: ['./service-orders-list.component.css']
})
export class ServiceOrdersListComponent implements OnInit {
  serviceOrders: ServiceOrder[] = [];
  loading: boolean = false;
  error: string = '';
  filtrosOpen: boolean = false;

  // Dashboard
  dashboardLoading: boolean = false;
  dashboardError: string = '';
  dashboardData: ServiceOrderDashboardData | null = null;

  // Paginação
  currentPage: number = 1;
  pageSize: number = 10;
  totalCount: number = 0;
  totalPages: number = 1;

  // Filtros
  selectedStatus: string = '';
  filterOrderNumber: string = '';
  filterPlate: string = '';
  filterStartDate: string = '';
  filterEndDate: string = '';
  selectedConfigOrderId: number | null = null;
  filterB2bContractId: string = '';
  filterIsScheduled: '' | 'true' | 'false' = '';

  // Opções de filtros
  statusOptions: StatusServiceOrder[] = [];
  configOrderOptions: ConfigOrderDTO[] = [];
  
  // IDs de ConfigOrders permitidos pelo token
  allowedConfigOrderIds: number[] = [];

  // Controle de paginação cliente (quando o usuário tem acesso a múltiplos tipos de OS)
  private isClientPaginated: boolean = false;
  private cachedAllOrders: ServiceOrder[] = [];

  tableColumns = [
    { label: 'numeroOS', titulo: 'Nº OS' },
    { label: 'motivoAbertura', titulo: 'Motivo Abertura', formatter: (value: any) => {
      const trimmed = (value ?? '').toString().trim();
      return trimmed || '—';
    }},
    {
      label: 'status',
      titulo: 'Status',
      type: 'chip',
      customClass: (value: any) => this.getStatusCellClass(value)
    },
    { label: 'dataAbertura', titulo: 'Data Abertura', type: 'date' },
    { label: 'veiculoPlaca', titulo: 'Veículo' },
    { label: 'setorAtual', titulo: 'Setor Atual' }
  ];

  tableActions = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-info',
      visible: true
    },
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-warning',
      visible: true
    }
  ];

  private departmentNameCache: Map<number, string> = new Map();

  // Exportação Excel
  showExportModal = false;
  isExporting = false;
  exportError: string | null = null;
  loadingExportColumns = false;
  availableExportColumns: { key: string; label: string; checked: boolean }[] = [];
  exportStartDate = '';
  exportEndDate = '';
  exportFilters = {
    status: '' as string | null,
    configOrderId: null as number | null,
    orderNumber: '' as string | null,
    plate: '' as string | null
  };

  constructor(
    private serviceOrdersService: ServiceOrdersService,
    private configOrdersService: ConfigOrdersService,
    private statusServiceOrdersService: StatusServiceOrdersService,
    private departmentService: DepartmentService,
    private authService: AuthService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.allowedConfigOrderIds = this.authService.getAllowedConfigOrderIdsFromToken();
    this.loadConfigOrders();
  }

  showDashboard(): void {
    this.filtrosOpen = false;
    this.loadDashboard();
  }

  showFilters(): void {
    this.filtrosOpen = true;
  }

  loadDashboard(): void {
    if (this.filtrosOpen) {
      return;
    }

    this.dashboardLoading = true;
    this.dashboardError = '';

    const ids = this.getAccessibleConfigOrderIds();
    if (ids.length === 0) {
      this.dashboardData = null;
      this.dashboardLoading = false;
      this.dashboardError = 'Nenhum tipo de OS permitido para exibir o dashboard.';
      return;
    }

    this.serviceOrdersService.getDashboard(ids).subscribe({
      next: (data) => {
        this.dashboardData = data;
        this.dashboardLoading = false;
      },
      error: () => {
        this.dashboardData = null;
        this.dashboardError = 'Erro ao carregar dashboard. Por favor, tente novamente.';
        this.dashboardLoading = false;
      }
    });
  }

  getDashboardCardVariant(status: string, index: number): string {
    const normalized = this.normalizeStatus(status);

    if (
      normalized === 'encerrada' ||
      normalized === 'encerrado' ||
      normalized === 'finalizada' ||
      normalized === 'finalizado' ||
      normalized === 'concluida' ||
      normalized === 'concluido'
    ) {
      return 'success';
    }

    if (
      normalized === 'cancelada' ||
      normalized === 'cancelado' ||
      normalized === 'reprovada' ||
      normalized === 'reprovado' ||
      normalized.includes('reprovad')
    ) {
      return 'danger';
    }

    if (
      normalized.includes('andamento') ||
      normalized.includes('execucao') ||
      normalized.includes('em curso') ||
      normalized.includes('em atendimento')
    ) {
      return 'info';
    }

    if (
      normalized.includes('aberta') ||
      normalized.includes('aberto') ||
      normalized.includes('pendente') ||
      normalized.includes('aguardando') ||
      normalized.includes('aguarda')
    ) {
      return 'warning';
    }

    const variants = ['primary', 'info', 'warning', 'danger'];
    return variants[index % variants.length];
  }

  getDashboardChartVariant(index: number): 'line' | 'area' | 'bar' {
    const variants: Array<'line' | 'area' | 'bar'> = ['line', 'line', 'area', 'bar'];
    return variants[index % variants.length];
  }

  private getAccessibleConfigOrderIds(): number[] {
    const fromOptions = this.configOrderOptions
      .map(c => c.id)
      .filter((id): id is number => id != null && id > 0);
    return fromOptions.length > 0 ? fromOptions : this.allowedConfigOrderIds;
  }

  loadConfigOrders(): void {
    this.configOrdersService.getAll().subscribe({
      next: (data) => {
        const tokenIds = this.authService.getAllowedConfigOrderIdsFromToken();
        this.allowedConfigOrderIds = mergeAccessibleConfigOrderIds(tokenIds, data);
        this.configOrderOptions = filterAccessibleConfigOrders(data, this.allowedConfigOrderIds);

        if (
          this.selectedConfigOrderId &&
          !this.allowedConfigOrderIds.includes(this.selectedConfigOrderId)
        ) {
          this.selectedConfigOrderId = null;
        }

        // Um carregamento por vez: lista primeiro; dashboard só depois (evita 2 spinners).
        this.loadServiceOrders();
      },
      error: () => {}
    });

    this.statusServiceOrdersService.getAll().subscribe({
      next: (data) => {
        const seen = new Set<string>();
        this.statusOptions = data.filter(s => {
          if (seen.has(s.name)) return false;
          seen.add(s.name);
          return true;
        });
      },
      error: () => {}
    });
  }

  loadServiceOrders(): void {
    this.loading = true;
    this.error = '';

    if (this.allowedConfigOrderIds.length === 0) {
      this.serviceOrders = [];
      this.cachedAllOrders = [];
      this.totalCount = 0;
      this.totalPages = 1;
      this.currentPage = 1;
      this.loading = false;
      this.error = 'Nenhum tipo de OS permitido para este usuário.';
      return;
    }

    const baseFilters: any = {};
    if (this.selectedStatus) baseFilters.status = this.selectedStatus;
    if (this.filterOrderNumber?.trim()) baseFilters.orderNumber = this.filterOrderNumber.trim();
    if (this.filterPlate?.trim()) baseFilters.plate = this.filterPlate.trim();
    if (this.filterStartDate) baseFilters.startDate = new Date(this.filterStartDate).toISOString();
    if (this.filterEndDate) baseFilters.endDate = new Date(this.filterEndDate + 'T23:59:59').toISOString();
    const b2bId = Number(this.filterB2bContractId);
    if (Number.isFinite(b2bId) && b2bId > 0) baseFilters.b2bContractId = b2bId;
    if (this.filterIsScheduled === 'true') baseFilters.isScheduled = true;
    if (this.filterIsScheduled === 'false') baseFilters.isScheduled = false;

    if (this.selectedConfigOrderId) {
      // Filtro específico selecionado pelo usuário
      if (!this.allowedConfigOrderIds.includes(this.selectedConfigOrderId)) {
        this.serviceOrders = [];
        this.totalCount = 0;
        this.totalPages = 1;
        this.loading = false;
        return;
      }
      this.isClientPaginated = false;
      this.cachedAllOrders = [];
      this.fetchServerPaginated({ ...baseFilters, configOrderId: this.selectedConfigOrderId });
    } else if (this.allowedConfigOrderIds.length === 1) {
      // Apenas um tipo permitido — paginação servidor normal
      this.isClientPaginated = false;
      this.cachedAllOrders = [];
      this.fetchServerPaginated({ ...baseFilters, configOrderId: this.allowedConfigOrderIds[0] });
    } else {
      // Múltiplos tipos permitidos — busca paralela + paginação cliente
      this.isClientPaginated = true;
      this.fetchAllAndPaginateClientSide(baseFilters);
    }
  }

  private fetchServerPaginated(filters: any): void {
    this.serviceOrdersService.getPaginated(this.currentPage, this.pageSize, filters).subscribe({
      next: (res) => {
        const sorted = (res.serviceOrders || []).slice().sort((a, b) => {
          const da = a.dataAbertura ? new Date(a.dataAbertura).getTime() : 0;
          const db = b.dataAbertura ? new Date(b.dataAbertura).getTime() : 0;
          if (db !== da) return db - da;
          return (b.id ?? 0) - (a.id ?? 0);
        });
        this.serviceOrders = this.transformServiceOrders(sorted);
        this.totalCount = res.totalCount;
        this.currentPage = res.page;
        this.pageSize = res.pageSize;
        this.totalPages = Math.max(1, res.totalPages);
        this.resolveDepartmentNames();
        this.finishListLoad();
      },
      error: (err) => {
        console.error('Erro ao carregar ordens de serviço:', err);
        this.error = 'Erro ao carregar ordens de serviço. Por favor, tente novamente.';
        this.finishListLoad();
      }
    });
  }

  /** Encerra o spinner da lista e, se estiver na aba dashboard, carrega KPIs em seguida. */
  private finishListLoad(): void {
    this.loading = false;
    if (!this.filtrosOpen) {
      this.loadDashboard();
    }
  }

  /**
   * Busca todas as OS permitidas em paralelo (uma requisição por configOrderId),
   * mescla, ordena e aplica paginação no lado cliente.
   * Utilizado quando o usuário tem acesso a múltiplos tipos de OS, pois o
   * backend aceita apenas um configOrderId por chamada.
   */
  private fetchAllAndPaginateClientSide(baseFilters: any): void {
    const requests = this.allowedConfigOrderIds.map(id =>
      this.serviceOrdersService
        .getPaginated(1, MAX_ORDERS_PER_CONFIG, { ...baseFilters, configOrderId: id })
        .pipe(
          map(res => res.serviceOrders),
          catchError(() => of([] as ServiceOrder[]))
        )
    );

    forkJoin(requests).subscribe({
      next: (results) => {
        const seen = new Set<number>();
        const merged: ServiceOrder[] = [];
        for (const list of results) {
          for (const os of list) {
            if (os.id != null && !seen.has(os.id)) {
              seen.add(os.id);
              merged.push(os);
            }
          }
        }

        // Mais recentes primeiro
        merged.sort((a, b) => {
          const da = a.dataAbertura ? new Date(a.dataAbertura).getTime() : 0;
          const db = b.dataAbertura ? new Date(b.dataAbertura).getTime() : 0;
          return db - da;
        });

        this.cachedAllOrders = merged;
        this.totalCount = merged.length;
        this.currentPage = 1;
        this.totalPages = Math.max(1, Math.ceil(merged.length / this.pageSize));
        this.applyClientPagination();
        this.finishListLoad();
      },
      error: () => {
        this.error = 'Erro ao carregar ordens de serviço. Por favor, tente novamente.';
        this.finishListLoad();
      }
    });
  }

  private applyClientPagination(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const page = this.cachedAllOrders.slice(start, start + this.pageSize);
    this.serviceOrders = this.transformServiceOrders(page);
    this.resolveDepartmentNames();
  }

  /**
   * Transforma as ordens de serviço adicionando propriedades calculadas
   */
  transformServiceOrders(orders: ServiceOrder[]): any[] {
    return orders.map(os => ({
      ...os,
      setorAtual: this.getSetorAtual(os),
      tipoOSNome: os.configOrder?.typeOS || os.tipoOS || os.tipo || 'N/A',
      _actions: this.getActionsForOS(os)
    }));
  }

  getActionsForOS(os: ServiceOrder): any[] {
    if (os.status === 'Encerrada') {
      return this.tableActions.filter(a => a.action !== 'edit');
    }
    return this.tableActions;
  }

  /**
   * Retorna o setor atual da OS baseado no histórico ou setor de abertura
   */
  getSetorAtual(os: ServiceOrder): string {
    // Se há histórico, procura o último direcionamento
    if (os.historico && os.historico.length > 0) {
      const ultimoDirecionamento = [...os.historico]
        .reverse()
        .find(h => h.tipo === 'direcionamento' && h.setorDestino);
      
      if (ultimoDirecionamento?.setorDestino) {
        return ultimoDirecionamento.setorDestino;
      }
    }

    // Verifica cache de departamento pelo unidadeId
    const deptId = os.unidadeId ?? (os as any).departmentId ?? (os as any).DepartmentId;
    if (deptId && this.departmentNameCache.has(deptId)) {
      return this.departmentNameCache.get(deptId)!;
    }
    
    // Fallback para o setor de abertura
    return os.setorAbertura || 'Recepção';
  }

  /**
   * Resolve os nomes dos departamentos para todas as OS da lista
   */
  private resolveDepartmentNames(): void {
    const deptIds = new Set<number>();
    this.serviceOrders.forEach(os => {
      const deptId = os.unidadeId ?? (os as any).departmentId ?? (os as any).DepartmentId;
      if (deptId && !this.departmentNameCache.has(deptId)) {
        deptIds.add(deptId);
      }
    });

    if (deptIds.size === 0) return;

    const requests = Array.from(deptIds).map(id =>
      this.departmentService.getDepartmentById(id).pipe(
        map(dept => ({ id, name: dept.name })),
        catchError(() => of({ id, name: '' }))
      )
    );

    forkJoin(requests).subscribe(results => {
      results.forEach(r => {
        if (r.name) this.departmentNameCache.set(r.id, r.name);
      });
      // Atualiza setorAtual com os nomes resolvidos
      this.serviceOrders = this.serviceOrders.map(os => ({
        ...os,
        setorAtual: this.getSetorAtual(os)
      }));
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadServiceOrders();
  }

  clearFilters(): void {
    this.selectedStatus = '';
    this.filterOrderNumber = '';
    this.filterPlate = '';
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.selectedConfigOrderId = null;
    this.filterB2bContractId = '';
    this.filterIsScheduled = '';
    this.currentPage = 1;
    this.loadServiceOrders();
  }

  hasActiveFilters(): boolean {
    return !!(
      this.selectedStatus ||
      this.filterOrderNumber ||
      this.filterPlate ||
      this.filterStartDate ||
      this.filterEndDate ||
      this.selectedConfigOrderId ||
      this.filterB2bContractId ||
      this.filterIsScheduled
    );
  }

  getConfigOrderName(id: number): string {
    return this.configOrderOptions.find(c => c.id === id)?.typeOS || '';
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    if (this.isClientPaginated) {
      this.applyClientPagination();
    } else {
      this.loadServiceOrders();
    }
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    if (this.isClientPaginated) {
      this.totalPages = Math.max(1, Math.ceil(this.cachedAllOrders.length / this.pageSize));
      this.applyClientPagination();
    } else {
      this.loadServiceOrders();
    }
  }

  onTableAction(event: { action: string, item: ServiceOrder }): void {
    switch (event.action) {
      case 'view':
        this.viewServiceOrder(event.item);
        break;
      case 'edit':
        this.editServiceOrder(event.item);
        break;
    }
  }

  viewServiceOrder(serviceOrder: ServiceOrder): void {
    if (!serviceOrder?.id) return;
    // Abre a OS completa em modo leitura (não o modal resumido de consulta).
    this.router.navigate(['/service-orders', serviceOrder.id, 'view']);
  }

  editServiceOrder(serviceOrder: ServiceOrder): void {
    if (serviceOrder.status === 'Encerrada') {
      this.confirmationService.confirm({
        title: 'Ação não permitida',
        message: 'Não é possível editar uma Ordem de Serviço encerrada.',
        type: 'warning',
        confirmText: 'Ok',
        cancelText: ''
      }).subscribe();
      return;
    }
    this.router.navigate(['/service-orders', serviceOrder.id, 'edit']);
  }

  createNewServiceOrder(): void {
    this.router.navigate(['/service-orders/new']);
  }

  canCreateServiceOrder(): boolean {
    return this.allowedConfigOrderIds.length > 0;
  }

  onRowClick(item: ServiceOrder): void {
    this.viewServiceOrder(item);
  }

  private getStatusCellClass(status: any): string {
    const normalized = this.normalizeStatus(status);
    if (!normalized) {
      return 'os-status-cell os-status--neutral';
    }

    // Fechados / concluídos
    if (
      normalized === 'encerrada' ||
      normalized === 'encerrado' ||
      normalized === 'finalizada' ||
      normalized === 'finalizado' ||
      normalized === 'concluida' ||
      normalized === 'concluido'
    ) {
      return 'os-status-cell os-status--success';
    }

    // Cancelados / reprovados
    if (
      normalized === 'cancelada' ||
      normalized === 'cancelado' ||
      normalized === 'reprovada' ||
      normalized === 'reprovado'
    ) {
      return 'os-status-cell os-status--danger';
    }

    // Em andamento
    if (
      normalized.includes('andamento') ||
      normalized.includes('execucao') ||
      normalized.includes('em curso') ||
      normalized.includes('em atendimento')
    ) {
      return 'os-status-cell os-status--info';
    }

    // Abertos / pendentes
    if (
      normalized.includes('aberta') ||
      normalized.includes('aberto') ||
      normalized.includes('pendente') ||
      normalized.includes('aguardando') ||
      normalized.includes('aguarda')
    ) {
      return 'os-status-cell os-status--warning';
    }

    return 'os-status-cell os-status--neutral';
  }

  private normalizeStatus(value: any): string {
    if (value === null || value === undefined) return '';
    const text = String(value).trim().toLowerCase();
    if (!text) return '';

    // Remove acentos (compatível com browsers antigos)
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  // ===== Exportação Excel =====

  openExportModal(): void {
    this.showExportModal = true;
    this.exportError = null;
    this.exportStartDate = this.filterStartDate || '';
    this.exportEndDate = this.filterEndDate || '';
    this.exportFilters = {
      status: this.selectedStatus || null,
      configOrderId: this.selectedConfigOrderId,
      orderNumber: this.filterOrderNumber?.trim() || null,
      plate: this.filterPlate?.trim() || null
    };
    this.loadingExportColumns = true;
    this.availableExportColumns = [];

    this.serviceOrdersService.getExportColumns().subscribe({
      next: (cols) => {
        this.availableExportColumns = cols.map(c => ({
          key: c.key,
          label: c.label,
          checked: isDefaultServiceOrderExportKey(c.key)
        }));
        this.loadingExportColumns = false;
      },
      error: () => {
        this.applyFallbackExportColumns();
        this.loadingExportColumns = false;
      }
    });
  }

  private applyFallbackExportColumns(): void {
    this.availableExportColumns = FALLBACK_SERVICE_ORDER_EXPORT_COLUMNS.map(key => ({
      key,
      label: SERVICE_ORDER_EXPORT_COLUMN_LABELS[key] ?? key,
      checked: isDefaultServiceOrderExportKey(key)
    }));
  }

  closeExportModal(): void {
    this.showExportModal = false;
    this.exportError = null;
    this.availableExportColumns = [];
  }

  toggleExportField(key: string): void {
    const field = this.availableExportColumns.find(f => f.key === key);
    if (field) {
      field.checked = !field.checked;
    }
  }

  selectAllExportFields(): void {
    this.availableExportColumns.forEach(f => f.checked = true);
  }

  deselectAllExportFields(): void {
    this.availableExportColumns.forEach(f => f.checked = false);
  }

  exportToExcel(): void {
    const columns = this.availableExportColumns
      .filter(f => f.checked)
      .map(f => f.key);

    if (columns.length === 0) {
      this.exportError = 'Selecione pelo menos uma coluna para exportar.';
      return;
    }

    if (this.exportStartDate && this.exportEndDate && this.exportStartDate > this.exportEndDate) {
      this.exportError = 'A data início não pode ser posterior à data fim.';
      return;
    }

    this.exportError = null;
    this.isExporting = true;

    const body: ServiceOrderExportRequest = {
      columns,
      startDate: this.exportStartDate ? new Date(this.exportStartDate).toISOString() : null,
      endDate: this.exportEndDate ? new Date(this.exportEndDate + 'T23:59:59').toISOString() : null,
      status: this.exportFilters.status?.trim() || null,
      orderNumber: this.exportFilters.orderNumber?.trim() || null,
      plate: this.exportFilters.plate?.trim() || null
    };

    if (this.exportFilters.configOrderId) {
      body.configOrderId = this.exportFilters.configOrderId;
    } else if (this.allowedConfigOrderIds.length > 1) {
      body.configOrderIds = [...this.allowedConfigOrderIds];
    } else if (this.allowedConfigOrderIds.length === 1) {
      body.configOrderId = this.allowedConfigOrderIds[0];
    }

    this.serviceOrdersService.exportToExcel(body).subscribe({
      next: (result) => {
        this.downloadExcelFile(result.blob, result.fileName);
        this.isExporting = false;
        this.closeExportModal();
      },
      error: () => {
        this.isExporting = false;
        this.exportError = 'Erro ao gerar planilha Excel.';
      }
    });
  }

  private downloadExcelFile(blob: Blob, fileName?: string): void {
    try {
      let excelBlob = blob;
      if (!blob.type || !blob.type.includes('spreadsheet')) {
        excelBlob = new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      }
      const url = window.URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `exportacao_ordens_servico_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      this.errorService.showSuccessMessage('Exportação Excel gerada e baixada com sucesso!');
    } catch {
      this.exportError = 'Erro ao fazer download do arquivo Excel.';
    }
  }
}

